import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { EvSection, EvSwitch, EvTitle, OptionType, useSkipLinks } from '@evinced-private/ui-common';

import {
	WEB_SDK_TESTS_PAGE_MAIN_TITLE_ID,
	WEB_SDK_TESTS_PAGE_TABLE_SECTION_ID,
	WEB_SDK_TESTS_PAGE_TABLE_TITLE_ID,
	WEB_SDK_TESTS_PAGE_USAGE_SECTION_ID,
	WEB_SDK_TESTS_PAGE_USAGE_TITLE_ID
} from 'data-automation-ids/WebSdkDataAutomationIds';
import { SummaryLineSkeleton } from 'src/apps/web-sdks/components/skeletons/summary-line-skeleton/SummaryLineSkeleton';
import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { HorizontalDivider } from 'src/common/components/horizontal-divider/HorizontalDivider';
import { LabelsManagementTableActions } from 'src/common/components/labels-management-table-actions';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { SummaryPane } from 'src/common/components/summary-pane/SummaryPane';
import { getUsageDataTooltipText } from 'src/common/helpers/TooltipContentHelper';
import { useEnvironmentConfig } from 'src/common/hooks/useEnvironmentConfig';
import { WEB_SDK_TEST_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import {
	showRecentTestKey,
	showTestPathKey
} from 'src/common/providers/dashboard/TablesCustomStateHelper';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import {
	USAGE_DATA,
	WEB_SDK_TEST_LABELS,
	WEB_SDK_TESTS
} from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { usePropertiesMapQuery } from 'src/common/services/properties/hooks/usePropertiesMapQuery';
import { DevelopmentToggles, getSingleToggle } from 'src/common/services/TogglesService';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { WebSdkTestsSkeleton } from '../../components/skeletons';
import {
	ADD_LABELS_BUTTON_LABEL,
	CHANGE_LABEL_VALUE_BUTTON_LABEL,
	REMOVE_LABELS_BUTTON_LABEL,
	SELECT_TESTS_PROMPT,
	TESTS_PERIOD_OPTIONS
} from '../../helpers/WebSdkConsts';
import { populateTestsTableDataWithDisplayData } from '../../helpers/WebSdkHelper';
import { getWebSdkTestsColumns } from '../../helpers/WebSdkTableHelper';
import { useWebSdkTestLabelsQuery } from '../../hooks/useWebSdkTestLabelsQuery';
import { useWebSdkTestsQuery } from '../../hooks/useWebSdkTestsQuery';
import { useWebSdkUsageDataQuery } from '../../hooks/useWebSdkUsageDataQuery';
import { WebSdkTestsPageError } from '../error-pages/WebSdkTestsPageError';

import { FilterSection } from './components/filter-section/FilterSection';
import { FilterSectionV2 } from './components/filter-section-v2/FilterSectionV2';
import { RemoveLabelTestsModal } from './components/remove-label-tests-modal/RemoveLabelTestsModal';
import { UpdateTestsModal } from './components/update-tests-modal/UpdateTestsModal';
import { useFilterSectionV2 } from './hooks/useFilterSectionV2';
import { useProcessingStatus } from './hooks/useProcessingStatus';

import './WebSdkTestsPage.scss';

const WebSdkPageTitle = 'Web SDK Report';
const TimePeriodText = 'All tests';
const TimePeriodTextAlt = 'all time';
const SummaryTitle = 'Summary';
const LabelRemovalErrorMessage = 'Failed to remove labels';
const LabelRemovalProgressMessage = 'Removing labels';
const LabelUpdateErrorMessage = 'Failed to update labels';
const LabelUpdateProgressMessage = 'Updating labels';

const enableWebSdkProperties = getSingleToggle(DevelopmentToggles.ENABLE_WEB_SDK_PROPERTIES);

const formatSummarySubtitle = (testPeriodLabel: string, selectedLabels: unknown[]): string => {
	const timePeriodText =
		testPeriodLabel === TimePeriodText
			? TimePeriodTextAlt
			: testPeriodLabel.toLowerCase().replace('last', 'the last');
	const labelCount = selectedLabels?.length || 0;

	if (labelCount === 0) {
		return `Showing data from ${timePeriodText}`;
	}

	const labelText = labelCount === 1 ? '1 label' : `${labelCount} labels`;
	return `Showing data from ${timePeriodText}, filtered by ${labelText}`;
};

export const WebSdkTestsPage: FC = () => {
	const [isUpdateLabelModalOpen, setIsUpdateLabelModalOpen] = useState(false);
	const [isRemoveLabelModalOpen, setIsRemoveLabelModalOpen] = useState(false);
	const [selectedRows, setSelectedRows] = useState<IWebSdkTest[]>([]);
	const [tableKey, setTableKey] = useState(0);
	const [showInitialSkeleton, setShowInitialSkeleton] = useState(true);
	const { onTableParamStatusChange, getTableStateById } = useTableState();
	const tableState = getTableStateById(WEB_SDK_TEST_TABLE_ID);
	const { filters, tableSort, paginationInfo, customParams } = tableState;
	const showRecentTest = customParams?.[showRecentTestKey] ?? false;
	const showTestPath = customParams?.[showTestPathKey] ?? false;

	const handleClearSelection = useCallback((): void => {
		setSelectedRows([]);
		setTableKey((prev) => prev + 1);
	}, []);

	const handleProcessingComplete = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: [WEB_SDK_TESTS], refetchType: 'active' }),
			queryClient.invalidateQueries({ queryKey: [USAGE_DATA], refetchType: 'active' }),
			queryClient.invalidateQueries({ queryKey: [WEB_SDK_TEST_LABELS], refetchType: 'active' })
		]);

		handleClearSelection();
	}, [handleClearSelection]);

	const handleAddLabelSuccess = useCallback(() => {
		handleProcessingComplete();
	}, [handleProcessingComplete]);

	const { startPolling } = useProcessingStatus(handleProcessingComplete);

	const testLabelsQuery = useWebSdkTestLabelsQuery();

	const { unitIdDisplayName, unitIdLabelConfig, isNewPlatformEnabled, tenantConfiguredLabels } =
		usePlatformConfigStore();

	const { environmentDisplayName, environmentLabelConfig } = useEnvironmentConfig();

	const propertiesQuery = usePropertiesMapQuery(enableWebSdkProperties);

	const propertiesOptions = useMemo((): OptionType[] => {
		const options: OptionType[] = [];
		if (enableWebSdkProperties && !propertiesQuery.isLoading) {
			propertiesQuery.data?.forEach((name, id) => {
				options.push({
					value: id,
					label: name
				});
			});
		}

		return options;
	}, [propertiesQuery]);

	const usageDataQuery = useWebSdkUsageDataQuery({
		showRecentTest,
		testPeriod: filters?.testPeriod?.[0],
		searchValue: filters?.searchValue,
		labels: filters?.labels,
		isNewPlatformEnabled
	});

	useSkipLinks(!usageDataQuery.isLoading);

	const [testsQuery, invalidateTestsQuery] = useWebSdkTestsQuery({
		showRecentTest,
		filters,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		showTestPath,
		isNewPlatformEnabled
	});

	const showTestPathToggleButton = useMemo(
		(): ReactElement => (
			<EvSwitch
				label="Show test path"
				checked={showTestPath}
				onChange={() => {
					onTableParamStatusChange(WEB_SDK_TEST_TABLE_ID, showTestPathKey, () => !showTestPath);
					invalidateTestsQuery();
				}}
			/>
		),
		[showTestPath, onTableParamStatusChange, invalidateTestsQuery]
	);

	const total = testsQuery.data?.total ?? 0;
	const unfilteredTotal = testsQuery.data?.unfilteredTotal ?? 0;

	// Use FilterSectionV2 hook for new platform
	const { v2FilterOptions, v2Filters, handleV2FilterChange } = useFilterSectionV2({
		testLabelsData: testLabelsQuery.data,
		propertiesOptions,
		filters
	});

	const rows = useMemo(
		() =>
			populateTestsTableDataWithDisplayData(
				testsQuery.data?.rows ?? [],
				showTestPath,
				propertiesQuery.data
			),
		[testsQuery.data?.rows, showTestPath, propertiesQuery.data]
	);

	const columns = useMemo(
		() =>
			getWebSdkTestsColumns(
				isNewPlatformEnabled,
				unitIdDisplayName,
				unitIdLabelConfig,
				environmentDisplayName,
				environmentLabelConfig
			),
		[
			isNewPlatformEnabled,
			unitIdDisplayName,
			unitIdLabelConfig,
			environmentDisplayName,
			environmentLabelConfig
		]
	);

	const handleSelectRow = (row: IWebSdkTest, isSelect: boolean): void => {
		if (isSelect) {
			setSelectedRows((prev) => [...prev, row]);
		} else {
			setSelectedRows((prev) => prev.filter((r) => r !== row));
		}
	};

	const handleSelectAll = (isSelect: boolean, rows: IWebSdkTest[]): void => {
		setSelectedRows(isSelect ? rows : []);
	};

	const isPropertiesLoading = enableWebSdkProperties && propertiesQuery.isLoading;

	const isFilterOptionsLoading = isPropertiesLoading || testLabelsQuery.isLoading;

	const isTableLoading =
		isPropertiesLoading ||
		testsQuery.isLoading ||
		testsQuery.isPreviousData ||
		(testsQuery.isStale && testsQuery.isRefetching);

	const isPrimaryLoading = isFilterOptionsLoading || usageDataQuery.isLoading || isTableLoading;

	useEffect(() => {
		if (!isPrimaryLoading && showInitialSkeleton) {
			setShowInitialSkeleton(false);
		}
	}, [isPrimaryLoading, showInitialSkeleton]);

	// Memoize filterOptions
	const filterOptions = useMemo(
		() => ({
			labels: testLabelsQuery.data ?? [],
			properties: propertiesOptions ?? [],
			testPeriod: TESTS_PERIOD_OPTIONS
		}),
		[testLabelsQuery.data, propertiesOptions]
	);

	if (testsQuery.isError || usageDataQuery.isError || propertiesQuery.isError) {
		return <WebSdkTestsPageError />;
	}

	if (!testsQuery.isLoading && !testsQuery.isPreviousData && unfilteredTotal === 0) {
		return <WebSdkTestsPageError isNoData />;
	}

	// Show skeleton during initial loading (before any real content becomes available)
	if (showInitialSkeleton) {
		return <WebSdkTestsSkeleton />;
	}

	return (
		<div className="web-sdk-tests-page">
			<EvTitle
				titleText={WebSdkPageTitle}
				dataAutomationId={WEB_SDK_TESTS_PAGE_MAIN_TITLE_ID}
				headingLevel={1}
				className="main-title page-heading"
			/>
			{isNewPlatformEnabled ? (
				<FilterSectionV2
					isFilterOptionsLoading={isFilterOptionsLoading}
					filterOptions={v2FilterOptions}
					filters={v2Filters}
					showRecentTest={showRecentTest}
					onShowRecentTestChange={() =>
						onTableParamStatusChange(WEB_SDK_TEST_TABLE_ID, 'showRecentTest', () => !showRecentTest)
					}
					showTestPath={showTestPath}
					unitIdDisplayName={unitIdDisplayName}
					onFilterChange={handleV2FilterChange}
				/>
			) : (
				<FilterSection
					isFilterOptionsLoading={isFilterOptionsLoading}
					filterOptions={filterOptions}
					filters={filters}
					showRecentTest={showRecentTest}
					onShowRecentTestChange={() =>
						onTableParamStatusChange(WEB_SDK_TEST_TABLE_ID, 'showRecentTest', () => !showRecentTest)
					}
					showTestPath={showTestPath}
				/>
			)}
			<HorizontalDivider />
			<EvSection
				ariaLabel="Web SDK usage"
				skipLinkId="usage-section"
				className={classNames('dashboard-section', 'usage-section')}
				ariaLive="polite"
				dataAutomationId={WEB_SDK_TESTS_PAGE_USAGE_SECTION_ID}
			>
				<EvTitle
					titleText={SummaryTitle}
					headingLevel={2}
					className="page-heading"
					dataAutomationId={WEB_SDK_TESTS_PAGE_USAGE_TITLE_ID}
					MoreInfoContent={<>{getUsageDataTooltipText('tests')}</>}
				/>
				<EvTitle
					titleText={formatSummarySubtitle(filters?.testPeriod?.[0]?.label, filters?.labels)}
					headingLevel={3}
					className="page-subheading"
				/>
				{usageDataQuery.isLoading ? (
					<SummaryLineSkeleton />
				) : (
					<SummaryPane
						items={usageDataQuery.data}
						shouldHaveSkipLink={false}
						className="base-summary-pane"
						ariaLabel="Web SDKs usage summary"
					/>
				)}
			</EvSection>
			<EvSection
				ariaLabel="WEB SDK Tests Table"
				skipLinkId="main-section"
				className={classNames('dashboard-section', 'table-section')}
				dataAutomationId={WEB_SDK_TESTS_PAGE_TABLE_SECTION_ID}
			>
				<EvTitle
					titleText="Uploaded tests"
					headingLevel={2}
					className="page-heading"
					dataAutomationId={WEB_SDK_TESTS_PAGE_TABLE_TITLE_ID}
				/>
				{isNewPlatformEnabled && (
					<LabelsManagementTableActions<IWebSdkTest>
						selectedRows={selectedRows}
						handleClearSelection={handleClearSelection}
						promptText={SELECT_TESTS_PROMPT}
						itemName="test"
						addLabelButtonLabel={ADD_LABELS_BUTTON_LABEL}
						changeLabelValueButtonLabel={CHANGE_LABEL_VALUE_BUTTON_LABEL}
						removeLabelsButtonLabel={REMOVE_LABELS_BUTTON_LABEL}
						onLabelActionSuccess={handleAddLabelSuccess}
						tenantConfiguredLabels={tenantConfiguredLabels}
					/>
				)}
				{isTableLoading ? (
					<TableSkeleton columns={5} rowCount={7} showActions={true} rowHeight="120px" />
				) : (
					<PlatformTable
						key={tableKey}
						isTableLoading={isTableLoading}
						tableId={WEB_SDK_TEST_TABLE_ID}
						tableData={rows}
						totalPaginationableResults={total}
						totalCount={unfilteredTotal}
						className="web-sdk-tests-table"
						persistTableState
						columns={columns}
						topRowActions={showTestPathToggleButton}
						options={{
							keyField: 'runId',
							title: 'Web SDK Tests',
							caption: 'Web SDK table',
							pagination: true,
							dataType: 'tests',
							remote: true,
							selectRow: isNewPlatformEnabled
								? {
										mode: 'checkbox',
										onSelect: handleSelectRow,
										onSelectAll: handleSelectAll
									}
								: undefined
						}}
					/>
				)}
			</EvSection>
			<UpdateTestsModal
				isOpen={isUpdateLabelModalOpen}
				onClose={() => setIsUpdateLabelModalOpen(false)}
				testRows={selectedRows}
				tenantConfiguredLabels={tenantConfiguredLabels}
				onSaveSuccess={() =>
					startPolling(
						LabelUpdateProgressMessage,
						`Labels updated for ${selectedRows.length} tests`,
						LabelUpdateErrorMessage
					)
				}
				onClearSelection={handleClearSelection}
			/>
			<RemoveLabelTestsModal
				isOpen={isRemoveLabelModalOpen}
				onClose={() => setIsRemoveLabelModalOpen(false)}
				testRows={selectedRows}
				onSaveSuccess={() =>
					startPolling(
						LabelRemovalProgressMessage,
						`Labels removed from ${selectedRows.length} tests`,
						LabelRemovalErrorMessage
					)
				}
			/>
		</div>
	);
};
